import {
  ActionIcon,
  Anchor,
  Badge,
  Group,
  Table,
  Text,
  Tooltip
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconChevronRight, IconPencil } from '@tabler/icons-react'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useNavigate } from 'react-router'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetDeviceDetailsById } from '@/queries/deviceQueries'
import {
  ApplicationNestedPath,
  buildAppLink,
  buildCreateDeploymentLink
} from '@/router/paths'
import { DeploymentOverviewApp } from '@/types/deploymentGroup'
import { CrudAction, Resource } from '@/types/permissions'
import { DeploymentOutputList } from '../DeploymentList/DeploymentOutputList'
import { OnlineStatusCount } from '../DeploymentList/OnlineStatusCount/OnlineStatusCount'
import { DeploymentDeviceRows } from './DeploymentDeviceRows'
import { sortDevicesByLabel, summarizeApp } from './deploymentTreeHelpers'
import { DeploymentTreeSlots } from './deploymentTreeTypes'

const COLUMN_COUNT = 4

type DeploymentAppRowsProps = DeploymentTreeSlots & {
  app: DeploymentOverviewApp
  groupsEnabled: boolean
  highlightedStreamId?: string
  highlightedGroupId?: string
}

export const DeploymentAppRows = ({
  app,
  groupsEnabled,
  highlightedStreamId,
  highlightedGroupId,
  renderAppActions,
  ...slots
}: DeploymentAppRowsProps) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const [canUpdateDeployment] = useHasPermissions(Resource.Deployments, [
    CrudAction.Update
  ])
  const [isExpanded, { toggle }] = useDisclosure(true)

  const deviceIds = useMemo(
    () => app.devices.map((device) => device.device_id),
    [app.devices]
  )

  const devicesById = useGetDeviceDetailsById(deviceIds)

  const devices = useMemo(
    () => sortDevicesByLabel(app.devices, devicesById),
    [app.devices, devicesById]
  )

  const summary = useMemo(() => summarizeApp(app), [app])

  const loadedDevices = deviceIds.flatMap((id) => devicesById[id] ?? [])
  const onlineCount = loadedDevices.filter((device) => device.active).length

  return (
    <>
      <Table.Tr bg="gray.1" style={{ cursor: 'pointer' }} onClick={toggle}>
        <Table.Td colSpan={COLUMN_COUNT}>
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-expanded={isExpanded}
                aria-label={intl.formatMessage({
                  id: isExpanded
                    ? 'deployments.list.app.toggle.hide'
                    : 'deployments.list.app.toggle.show'
                })}
                onClick={(e) => {
                  e.stopPropagation()
                  toggle()
                }}
              >
                <IconChevronRight
                  size={16}
                  style={{
                    transform: isExpanded ? 'rotate(90deg)' : 'none',
                    transition: 'transform 150ms ease'
                  }}
                />
              </ActionIcon>

              <Text fw={700}>{app.application_name}</Text>

              <Badge size="sm">{app.primary_ml_model.name}</Badge>

              {app.secondary_ml_model && (
                <Badge size="sm" color="brand-secondary">
                  {app.secondary_ml_model.name}
                </Badge>
              )}

              <DeploymentOutputList services={app.event_services} />

              <Group gap={6} wrap="nowrap">
                <Text size="xs" c="dimmed">
                  <FormattedMessage
                    id="deployments.list.inputSource.cameras"
                    values={{ count: summary.cameras }}
                  />
                </Text>

                {summary.folders > 0 && (
                  <Text size="xs" c="dimmed">
                    <FormattedMessage
                      id="deployments.list.inputSource.folders"
                      values={{ count: summary.folders }}
                    />
                  </Text>
                )}

                {groupsEnabled && (
                  <Text size="xs" c="dimmed">
                    <FormattedMessage
                      id="deployments.list.app.groups"
                      values={{ count: summary.groups }}
                    />
                  </Text>
                )}

                {groupsEnabled && summary.ownContainer > 0 && (
                  <Text size="xs" c="dimmed">
                    <FormattedMessage
                      id="deployments.list.app.ownContainer"
                      values={{ count: summary.ownContainer }}
                    />
                  </Text>
                )}
              </Group>

              <OnlineStatusCount
                onlineCount={onlineCount}
                offlineCount={loadedDevices.length - onlineCount}
              />
            </Group>

            <Group gap="sm" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
              <Anchor
                component={Link}
                to={buildAppLink(
                  app.application_id,
                  ApplicationNestedPath.events
                )}
                size="xs"
                fw={700}
                c="blue.8"
              >
                <FormattedMessage id="deployments.list.viewEvents" />
              </Anchor>

              {canUpdateDeployment && (
                <Tooltip
                  label={
                    <FormattedMessage id="deployments.list.buttons.edit.tooltip" />
                  }
                >
                  <ActionIcon
                    variant="outline"
                    size="md"
                    radius="sm"
                    color="blue"
                    aria-label={intl.formatMessage({
                      id: 'deployments.list.buttons.edit.tooltip'
                    })}
                    onClick={() =>
                      void navigate(
                        buildCreateDeploymentLink(app.application_id)
                      )
                    }
                  >
                    <IconPencil style={{ width: '70%', height: '70%' }} />
                  </ActionIcon>
                </Tooltip>
              )}

              {renderAppActions?.(app)}
            </Group>
          </Group>
        </Table.Td>
      </Table.Tr>

      {isExpanded &&
        devices.map((overviewDevice) => (
          <DeploymentDeviceRows
            key={overviewDevice.device_id}
            app={app}
            overviewDevice={overviewDevice}
            device={devicesById[overviewDevice.device_id]}
            groupsEnabled={groupsEnabled}
            highlightedStreamId={highlightedStreamId}
            highlightedGroupId={highlightedGroupId}
            {...slots}
          />
        ))}
    </>
  )
}
